import React, { useEffect, useState } from 'react';
import { Container, Button, Card, Row, Col, Alert } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../components/context/AuthContext';
import Layout from '../../components/ui/Layout';
import NoIndexHelmet from '../../components/seo/NoIndexHelmet';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() || '';
/** Use GSI when client ID is set - avoids signInWithRedirect which causes blank screen on iOS after auth */
function shouldUseGSI() {
  if (!googleClientId) return false;
  if (typeof navigator === 'undefined') return false;
  return true;
}

export default function Login () {
  const navigate = useNavigate();
  const { login, loginWithGoogleCredential, currentUser } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [queryParams] = useSearchParams();

  const path = queryParams.get('path');

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { label: "Login" }
  ];

  useEffect(() => {
    if(currentUser && path) navigate(path);
    else if(currentUser) navigate('/');
  }, [currentUser, path, navigate]);

  async function handleGoogleSignIn(){
    try{
      setError('');
      setLoading(true);
      await login('', '', 'google');
    } catch (e) {
      
      // Provide user-friendly error messages
      let errorMessage = e.message || 'An unknown error occurred during sign-in.';
      
      if (e.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked by your browser. Please allow popups for this site and try again.';
      } else if (e.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in popup was closed. Please try again.';
      } else if (e.code === 'auth/redirect-cancelled-by-user') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (e.code === 'auth/redirect-operation-pending') {
        errorMessage = 'Sign-in in progress. Please wait.';
      } else if (e.code === 'auth/unauthorized-domain') {
        errorMessage = 'This domain is not authorized for Google sign-in. Please contact support.';
      } else if (e.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (e.code === 'auth/operation-not-allowed') {
        errorMessage = 'Google sign-in is not enabled. Please contact support.';
      } else if (e.code === 'auth/invalid-api-key') {
        errorMessage = 'Authentication configuration error. Please contact support.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }
  const handleGoBack = () => {
    if (path && path !== window.location.pathname) { 
        navigate(path); 
    } else {
        navigate(-1); 
    }
  };

  return (
    <Layout>
      <NoIndexHelmet title="Login | Pack My Cart" path="/login" />
      <motion.div 
        initial={{ y: -50, opacity: 0}}
        animate={{ y:0, opacity: 1}}
        exit={{ opacity: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 15 }}
      >
        <Container className='d-flex flex-column align-items-center justify-content-center' style={{ minHeight: "calc(100vh - 120px)" }}>
            <div className='w-100' style={{ maxWidth: "450px" }}>
                <Row className="align-items-center mb-1 w-100">
                    <Col xs="auto" style={{paddingLeft: 0}}>
                        <Button variant="outline-secondary" onClick={handleGoBack} className="mb-2">
                        &larr; Go Back
                        </Button>
                    </Col>
                    <Col  style={{paddingLeft: 0, paddingRight:0 }}>
                        <BreadcrumbNavigation items={breadcrumbItems} />
                    </Col>
                </Row>
            
                <Card className='shadow-sm'>
                    <Card.Body className="p-4">
                        <h2 className='text-center mb-3'>Log In or Sign Up</h2>
                        <p className='text-center text-muted mb-4'>Continue with Google</p>
                        
                        {/* Clear error when user starts typing or clicking */}
                        {error && (
                          <div className="text-center mb-3">
                            <Button 
                              variant="link" 
                              size="sm" 
                              onClick={() => setError('')}
                              className="text-muted p-0"
                            >
                              Clear Error
                            </Button>
                          </div>
                        )}
                        
                        {!loading && (
                          <div className='d-flex flex-column align-items-center gap-3'>
                            {shouldUseGSI() ? (
                              <GoogleLogin
                                onSuccess={(res) => {
                                  if (res?.credential) {
                                    setLoading(true);
                                    loginWithGoogleCredential(res.credential).catch((e) => {
                                      setError(e?.message || 'Sign-in failed');
                                    }).finally(() => setLoading(false));
                                  }
                                }}
                                onError={() => setError('Google sign-in failed')}
                                theme="filled_blue"
                                size="large"
                                text="signin_with"
                                shape="rectangular"
                              />
                            ) : (
                              <Button onClick={handleGoogleSignIn} variant="light" className='border d-flex align-items-center justify-content-center p-2'>
                                <img alt={'Google Logo'} src={'/btn_google_signin_dark_normal_web.png'} style={{height: '40px'}}/>
                              </Button>
                            )}
                          </div>
                        )}
                        {loading && <p className="text-center">Signing in...</p>}

                        {error && (
                          <Alert variant="danger" className="mt-3">
                            <div className="d-flex align-items-start">
                              <div className="me-2">
                                <i className="fas fa-exclamation-triangle"></i>
                              </div>
                              <div>
                                <strong>Authentication Error:</strong>
                                <br />
                                {error}
                                <br />
                                <small className="text-muted mt-2 d-block">
                                  Check browser console for detailed error information.
                                </small>
                              </div>
                            </div>
                          </Alert>
                        )}
                    </Card.Body>
                </Card>
                 <div className="w-100 text-center mt-3">
                    <Link to="/">Back to Homepage</Link>
                </div>
            </div>
        </Container>
      </motion.div>
    </Layout>
  );
}
